// CardImageLoader - Handles loading card art images for Phaser
import type { CardDefinition } from '../types';
import { getLogger } from '../utils/logger';

const log = getLogger('CardImageLoader');

export interface CardImageLoadResult {
  loaded: number;
  failed: number;
  total: number;
}

/**
 * Preloads card images into a Phaser scene.
 * Uses the card definition's imagePath to load the texture with the card's ID as key.
 */
export function preloadCardImages(
  scene: Phaser.Scene,
  cards: CardDefinition[],
  onProgress?: (loaded: number, total: number) => void
): void {
  const cardsWithImages = cards.filter(card => card.imagePath);
  const total = cardsWithImages.length;

  for (const card of cardsWithImages) {
    if (!card.imagePath) continue;

    // Skip if already loaded
    if (scene.textures.exists(card.id)) {
      continue;
    }

    // The imagePath is relative to /assets/, but we serve from public/
    // So "cards/09骷髏人/SKT0001_小骷.jpg" becomes "/assets/cards/09骷髏人/SKT0001_小骷.jpg"
    const fullPath = `/assets/${card.imagePath}`;

    scene.load.image(card.id, fullPath);
  }

  // Track progress
  scene.load.on('progress', (value: number) => {
    if (onProgress) {
      onProgress(Math.floor(value * total), total);
    }
  });
}

/**
 * Dynamically loads a single card image if not already loaded.
 * Returns a promise that resolves when the image is ready.
 */
export function loadCardImage(
  scene: Phaser.Scene,
  card: CardDefinition
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!card.imagePath) {
      resolve(false);
      return;
    }

    // Already loaded
    if (scene.textures.exists(card.id)) {
      resolve(true);
      return;
    }

    const fullPath = `/assets/${card.imagePath}`;

    scene.load.image(card.id, fullPath);

    scene.load.once('complete', () => {
      resolve(scene.textures.exists(card.id));
    });

    scene.load.once('loaderror', () => {
      log.warn(`Failed to load card image: ${card.id} from ${fullPath}`);
      resolve(false);
    });

    scene.load.start();
  });
}

/**
 * Batch load card images for a deck.
 * This is optimized for loading only the cards in a player's deck.
 */
export async function preloadDeckImages(
  scene: Phaser.Scene,
  cards: CardDefinition[],
  onProgress?: (loaded: number, total: number) => void
): Promise<CardImageLoadResult> {
  const uniqueCards = new Map<string, CardDefinition>();

  // Deduplicate cards by ID
  for (const card of cards) {
    if (card.imagePath && !uniqueCards.has(card.id)) {
      uniqueCards.set(card.id, card);
    }
  }

  const cardsToLoad = Array.from(uniqueCards.values()).filter(
    card => !scene.textures.exists(card.id)
  );

  const total = cardsToLoad.length;
  let loaded = 0;
  let failed = 0;

  if (total === 0) {
    return { loaded: 0, failed: 0, total: 0 };
  }

  return new Promise((resolve) => {
    for (const card of cardsToLoad) {
      const fullPath = `/assets/${card.imagePath ?? ''}`;
      scene.load.image(card.id, fullPath);
    }

    scene.load.on('filecomplete', () => {
      loaded++;
      if (onProgress) {
        onProgress(loaded, total);
      }
    });

    scene.load.on('loaderror', (file: { key: string }) => {
      failed++;
      log.warn(`Failed to load card image: ${file.key}`);
      if (onProgress) {
        onProgress(loaded + failed, total);
      }
    });

    scene.load.once('complete', () => {
      resolve({ loaded, failed, total });
    });

    scene.load.start();
  });
}

/**
 * Check if a card image is loaded.
 */
export function isCardImageLoaded(scene: Phaser.Scene, cardId: string): boolean {
  return scene.textures.exists(cardId);
}

/**
 * Get the texture key for a card, or null if not loaded.
 */
export function getCardTextureKey(scene: Phaser.Scene, card: CardDefinition): string | null {
  if (scene.textures.exists(card.id)) {
    return card.id;
  }
  return null;
}
